import { resolveFtlOfferBaseUsd } from "@shared/utils/resolveFtlOfferBaseUsd";
import {
  sellPriceFromMarginFraction,
  sellPriceFromMarginPercent,
} from "@shared/utils/marginOnSell";
import { Email, LTLShipment, FTLShipment } from "@src/types/api";
import { emailShipmentsFtl, emailShipmentsLtl } from "@taskpane/utils/emailShipments";
import { formatCurrency } from "@taskpane/utils/shipmentUtils";
import {
  getLTLOriginDestination,
  getLTLWeight,
  getFTLOriginDestination,
  getFTLWeight,
} from "@taskpane/utils/shipmentUtils";
import type { LocationDisplay } from "@taskpane/utils/formUtils";
import { LoadStatus, StatusHistoryEntry } from "@src/types/api";
import { interactionLeadLocalPart } from "@taskpane/utils/interactionLeadDisplay";

export type ShipmentRowVM = {
  key: string;
  mode: "LTL" | "FTL";
  modeLabel: "LTL" | "FTL";
  shipmentId: string;
  origin: LocationDisplay;
  destination: LocationDisplay;
  detailLeft: string;
  weightLbs: number;
  isQuoted: boolean;
  replySelectionEnabled: boolean;
  priceText: string;
  loadStatus?: LoadStatus;
  statusHistory?: StatusHistoryEntry[];
  classification?: string | null;
  bolClassification?: string | null;
  failureContext?: string;
  interactionLeadLabel?: string;
  verifiedSender?: boolean;
  tmsLoadId?: string;
};

function marginPctFromOffer(offer: unknown): number {
  if (typeof offer !== "object" || offer === null) return 0;
  const m = (
    offer as {
      marginPct?: unknown;
    }
  ).marginPct;

  return typeof m === "number" && !Number.isNaN(m) ? m : 0;
}

export class ShipmentRowBuilder {
  static buildLTLRow(shipment: LTLShipment): ShipmentRowVM {
    const { origin, destination } = getLTLOriginDestination(shipment);
    const weight = getLTLWeight(shipment);
    const selectedIds = shipment.sentOffers ?? [];
    const isQuoted = selectedIds.length > 0;
    const priceText = this.computeLtlPriceText(shipment, selectedIds);

    return {
      key: `ltl-${shipment.shipmentId}`,
      mode: "LTL",
      modeLabel: "LTL",
      shipmentId: shipment.shipmentId,
      origin,
      destination,
      detailLeft: shipment.vehicle ?? shipment.requiredFields?.equipmentType ?? "N/A",
      weightLbs: weight,
      isQuoted,
      replySelectionEnabled: isQuoted,
      priceText,
      loadStatus: shipment.loadStatus,
      statusHistory: shipment.statusHistory,
      classification: shipment.classification,
      bolClassification: shipment.bolClassification,
      failureContext: shipment.failureContext,
      tmsLoadId: shipment.loadId || undefined,
    };
  }
  static buildFTLRow(shipment: FTLShipment): ShipmentRowVM {
    const { origin, destination } = getFTLOriginDestination(shipment);
    const weight = getFTLWeight(shipment);
    const list = shipment.quotedRates ?? [];
    const isQuoted = (shipment.sentOffers ?? []).length > 0 || !!shipment.selectedQuoteId;
    const priceText = this.computeFtlPriceText(shipment);

    return {
      key: `ftl-${shipment.shipmentId}`,
      mode: "FTL",
      modeLabel: "FTL",
      shipmentId: shipment.shipmentId,
      origin,
      destination,
      detailLeft:
        shipment.requiredFields?.equipmentType ?? shipment.requiredFields?.transportType ?? "N/A",
      weightLbs: weight,
      isQuoted,
      replySelectionEnabled: list.length > 0,
      priceText,
      loadStatus: shipment.loadStatus,
      statusHistory: shipment.statusHistory,
      classification: shipment.classification,
      bolClassification: shipment.bolClassification,
      failureContext: shipment.failureContext,
      tmsLoadId: shipment.loadId || undefined,
    };
  }
  static buildAllRows(email: Email | null, verifiedSender?: boolean): ShipmentRowVM[] {
    if (!email) return [];
    const rows: ShipmentRowVM[] = [];

    emailShipmentsLtl(email).forEach((shipment) => {
      rows.push({
        ...this.buildLTLRow(shipment),
        interactionLeadLabel: interactionLeadLocalPart(email.sender),
        verifiedSender,
      });
    });
    emailShipmentsFtl(email).forEach((shipment) => {
      rows.push({
        ...this.buildFTLRow(shipment),
        interactionLeadLabel: interactionLeadLocalPart(email.sender),
        verifiedSender,
      });
    });
    return rows;
  }
  private static computeLtlPriceText(shipment: LTLShipment, selectedIds: string[]): string {
    if (selectedIds.length === 0) return "No Rate Selected";
    if (selectedIds.length === 1) {
      const offer = shipment.quotedRates?.find((o) => o.id === selectedIds[0]);

      if (!offer) return "No Rate Selected";
      const billed = (offer as { totalBilledRate?: unknown }).totalBilledRate;

      if (typeof billed === "number" && billed > 0) return formatCurrency(billed);
      const marginPct = marginPctFromOffer(offer);
      const price = sellPriceFromMarginPercent(offer.price, marginPct);

      return price ? formatCurrency(price) : "No Rate Selected";
    }
    return `${selectedIds.length} offers`;
  }
  private static computeFtlPriceText(shipment: FTLShipment): string {
    const list = shipment?.quotedRates ?? [];
    const selectedId = shipment?.selectedQuoteId ?? null;
    const rate = (selectedId ? list.find((o) => o.id === selectedId) : undefined) ?? list[0];

    if (!rate) return "No Rate Selected";
    if (typeof rate.totalBilledRate === "number" && rate.totalBilledRate > 0) {
      return formatCurrency(rate.totalBilledRate);
    }
    const total = resolveFtlOfferBaseUsd(rate);
    const applied =
      typeof rate.appliedMargin === "number" && !Number.isNaN(rate.appliedMargin)
        ? rate.appliedMargin
        : undefined;
    const marginPct =
      typeof rate.marginPct === "number" && !Number.isNaN(rate.marginPct) ? rate.marginPct : 0;
    const adjustedTotal =
      applied != null && applied > 0 && applied < 1
        ? sellPriceFromMarginFraction(total, applied)
        : sellPriceFromMarginPercent(total, marginPct);

    return adjustedTotal ? formatCurrency(adjustedTotal) : "No Rate Selected";
  }
}
